import { Signal } from "../common/signal";
import { WebRTC } from "../webrtc/webrtc";
import { GeneralController } from "../webrtc_controller/general_controller";
import { ContextArgs } from "./contextArgs";
import { EmitterBase } from "../webrtc/event_emitter";
import { DataWebRTC } from "../webrtc/data_webrtc";
import { WebRTCBase } from "../webrtc/base";
import { StreamController } from "../webrtc_controller/stream_controller";
import { AudioWebRTC } from "../webrtc/audio_webrtc";
import { AppError } from "../common/app_error";
import "../webrtc/rtc_check";
import { VideoCodec } from "../webrtc/rtc_diagnosis";
const EdgeWebrtcConnLimit = "edge.webrtc.conn_limit";
const EdgebandwidthLimit = "edge.bandwidth_limit";
const EdgeSpeedTesting = "edge.speed_testing";
const EdgeOffline = "edge.camera_offline";
export var STREAM_STATE;
(function (STREAM_STATE) {
    STREAM_STATE["INIT"] = "init";
    STREAM_STATE["CONNECTED"] = "connected";
    STREAM_STATE["PLAYING"] = "playing";
    STREAM_STATE["RETRYING"] = "retrying";
    STREAM_STATE["ERROR"] = "error";
    STREAM_STATE["WEBRTC_RETRYING"] = "webRTCRetrying";
    STREAM_STATE["AUDIO_SPEAKABLE"] = "speakable";
    STREAM_STATE["AUDIO_SPEAK_STATE"] = "speaker.stats";
})(STREAM_STATE || (STREAM_STATE = {}));
// get sourceIdInner from sourceId
// sourceId is made by sourceIdInner-connectId-selfIncrementId
function secondLastIndexOf(str, char) {
    const lastIndex = str.lastIndexOf(char);
    if (lastIndex === -1)
        return ""; // Character not found
    const secondLastIndex = str.lastIndexOf(char, lastIndex - 1);
    if (secondLastIndex === -1)
        return "";
    return str.substring(0, secondLastIndex);
}
function indexOfoneOf(query, arr) {
    for (let i = 0; i < arr.length; i++) {
        if (query.indexOf(arr[i]) !== -1)
            return i;
    }
    return -1;
}
export class ControllerManager extends EmitterBase {
    // proxy the actual addSource method
    // this method is called as "play" in client side
    proxiedAddSource(sourceInfo, options, retryTime = 0) {
        const { onStateChange } = options;
        let id = this._controller.getUniqueSourceIdInner(sourceInfo.parameter, sourceInfo.streamType);
        id = id.substring(0, id.lastIndexOf('-'));
        const target = this.sourceIdCbMap.get(id);
        // the stream is deleted
        if (!target)
            return null;
        this?._controller?.addSources.call(this?._controller, [sourceInfo], (infos) => {
            // no sourceId means duplicated play
            // this context is modified, the following line is to make sure target is same as the one in the map, do not delete the following line. 
            const target = this.sourceIdCbMap.get(id);
            //target is deleted
            if (!target)
                return;
            if (infos?.streamInfo?.sourceId) {
                // upon situation of repeated play, trigger webrtc close and retry peer connection
                if (retryTime > 6) {
                    target.state = STREAM_STATE.ERROR;
                    console.log("retry limit reached,nvrId", this.nvrId);
                    const error = new AppError({ code: AppError.CODE_RETRY_LIMIT_REACH, name: AppError.NAME_RETRY_LIMIT_REACH, message: 'retry limit reached, original error:' + infos.reason });
                    onStateChange?.(STREAM_STATE.ERROR, target, error);
                    // fallback to close webrtc and retry, do not close pc for now
                    // this?._controller?.webRTC?.close()
                    return;
                }
                this.handleControllerRes(infos, target, retryTime);
            }
        });
        // return stream manupilating methods
        return {
            getInfo: () => {
                return target;
            },
            seek: (time, callback) => {
                const sourceId = target?.sourceId;
                const state = target?.state;
                if (sourceId && (state === STREAM_STATE.CONNECTED || state === STREAM_STATE.PLAYING)) {
                    this?._controller?.seek(time, sourceId, callback);
                    return { success: true };
                }
                else {
                    return { success: false, message: 'source not connected' };
                }
            },
            resume: (callback) => {
                const sourceId = target?.sourceId;
                const state = target?.state;
                if (sourceId && (state === STREAM_STATE.CONNECTED || state === STREAM_STATE.PLAYING)) {
                    this?._controller?.resume(sourceId, callback);
                    return { success: true };
                }
                else {
                    return { success: false, message: 'source not connected' };
                }
            },
            pause: (callback) => {
                const sourceId = target?.sourceId;
                const state = target?.state;
                if (sourceId && (state === STREAM_STATE.CONNECTED || state === STREAM_STATE.PLAYING)) {
                    this?._controller?.pause(sourceId, callback);
                    return { success: true };
                }
                else {
                    return { success: false, message: 'source not connected' };
                }
            },
            speed: (speed, cb) => {
                const sourceId = target?.sourceId;
                const state = target?.state;
                if (sourceId && (state === STREAM_STATE.CONNECTED || state === STREAM_STATE.PLAYING)) {
                    this?._controller?.speed(speed, sourceId, cb);
                    return { success: true };
                }
                else {
                    return { success: false, message: 'source not connected' };
                }
            },
            remove: () => {
                // const target = this.sourceIdCbMap.get(id)
                const sourceId = target?.sourceId;
                if (sourceId) {
                    this?._controller?.removeSources([sourceId]);
                    this.sourceIdCbMap.delete(id);
                    return { success: true };
                }
                else {
                    console.log("removing but source not connected", this.connectingPromise);
                    if (this.connectingPromise)
                        this.connectingPromise.then(() => this._controller.removeWithSourceIdInner(id));
                    else
                        this._controller.removeWithSourceIdInner(id);
                    this.sourceIdCbMap.delete(id);
                    return { success: true };
                }
            },
            releaseDevice: (kind) => {
                const sourceId = target?.sourceId;
                const state = target?.state;
                if (sourceId && (state === STREAM_STATE.CONNECTED || state === STREAM_STATE.AUDIO_SPEAKABLE)) {
                    this?._controller?.releaseDevice(sourceId, kind);
                    return { success: true };
                }
                else {
                    return { success: false, message: 'source not connected' };
                }
            }
        };
    }
    //handle the result of addSource
    handleControllerRes(infos, target, retryTime) {
        const onStateChange = target.options.onStateChange;
        const options = target.options;
        const canvasContainer = target.canvasContainer;
        if (!infos?.success) {
            console.log("fail reason", infos.reason);
            //special case for bandwidth limit, retry in other cases
            if (infos.reason instanceof AppError) {
                const name = infos.reason.name;
                if ([EdgebandwidthLimit, EdgeWebrtcConnLimit, EdgeSpeedTesting, EdgeOffline].includes(name)) {
                    target.state = STREAM_STATE.ERROR;
                    onStateChange?.(STREAM_STATE.ERROR, target, infos.reason);
                    return;
                }
            }
            target.state = STREAM_STATE.RETRYING;
            this.retryIterator(infos?.streamInfo, options, retryTime);
            onStateChange?.(STREAM_STATE.RETRYING, target);
        }
        else {
            // success,replace the canvas with the success canvas
            target.sourceId = infos?.streamInfo?.sourceId;
            target.state = STREAM_STATE.CONNECTED;
            if (infos?.streamInfo?.canvas) {
                canvasContainer.innerHTML = "";
                canvasContainer?.appendChild(infos?.streamInfo?.canvas);
            }
            onStateChange?.(STREAM_STATE.CONNECTED, target);
            console.log("connect success", "sourceId:", infos?.streamInfo?.sourceId);
        }
    }
    setSourceIdCbMap(sources, options) {
        for (let source of sources) {
            const canvasContainer = source.canvas && source.canvas.parentNode;
            const sourceId = this._controller.getUniqueSourceIdInner(source.parameter, source.streamType);
            const sourceIdInner = sourceId.substring(0, sourceId.lastIndexOf('-'));
            this.sourceIdCbMap.set(sourceIdInner, { streamInfo: source, sourceId: null, options, canvasContainer, state: STREAM_STATE.INIT });
        }
    }
    // get controller and make sure the returning controller is the connected one
    async asyncGetController() {
        const that = this;
        const handler = {
            get: function (target, property) {
                if (property === 'addEventListener') {
                    return that.addEventListener.bind(that);
                }
                if (property === "orgId") {
                    return that.orgId;
                }
                if (property === "webRTC") {
                    return that.webRtcProxy;
                }
                if (property === "then" || property === "stats") {
                    return that._controller[property];
                }
                // wait for the controller to be connected
                if (this.connectingPromise) {
                    return async function () {
                        try {
                            await this.connectingPromise;
                            if (that?._controller[property] instanceof Function) {
                                // proxy the play function
                                if (property === 'play') {
                                    const sources = arguments[0];
                                    const options = arguments[2];
                                    that.setSourceIdCbMap([sources], options);
                                    return that.proxiedAddSource(sources, options);
                                }
                                return that?._controller[property].call(that?._controller, ...arguments);
                            }
                            else {
                                return that?._controller[property];
                            }
                        }
                        catch (e) {
                            console.error(e);
                            throw e;
                        }
                    };
                }
                else if (that?._controller[property] instanceof Function) {
                    // proxy the play function
                    if (property === 'play') {
                        return (sources, options) => {
                            that.setSourceIdCbMap([sources], options);
                            return that.proxiedAddSource(sources, options);
                        };
                    }
                    return that?._controller[property].bind(that._controller);
                }
                else {
                    return that?._controller[property];
                }
            },
        };
        if (this.connectingPromise) {
            await this.connectingPromise;
        }
        if (!this.inited) {
            this.inited = true;
            await this.initController(this.nvrId, this.deviceIdV2, this.orgId);
        }
        const proxy = new Proxy({}, handler);
        return proxy;
    }
    bindEventsProxy(_controller) {
        //proxy webRTC events to webRtcProxy
        const orginalEmitEvent = EmitterBase.prototype.emitEvent;
        _controller.webRTC.emitEvent = (event, ...args) => {
            orginalEmitEvent.call(_controller.webRTC, event, ...args);
            this.webRtcProxy.emitEvent(event, ...args);
        };
        //proxy controller events to manager
        _controller.emitEvent = (event, ...args) => {
            orginalEmitEvent.call(_controller, event, ...args);
            this.emitEvent(event, ...args);
        };
    }
    constructor(nvrId, orgId, deviceIdV2) {
        super();
        this.connectingPromise = null;
        this.inited = false;
        // key is the calculated by parameter and streamType, can be gotten from the second last index of "-" from sourceId
        // value is streamInfo, canvasContainer, sourceId, options, state
        this.sourceIdCbMap = new Map();
        this.nvrId = nvrId;
        this.deviceIdV2 = deviceIdV2;
        this.webRtcProxy = new EmitterBase();
        this.orgId = orgId;
    }
    async retryIterator(streamInfo, options, retryTime = 0) {
        if (this.connectingPromise) {
            await this.connectingPromise;
        }
        console.log('retryIterator attempt', retryTime, streamInfo);
        //construct new canvas and replace
        const originalCanvas = streamInfo.canvas;
        if (originalCanvas) {
            const newCanvas = streamInfo.canvas.cloneNode(true);
            streamInfo.canvas = newCanvas;
        }
        this.proxiedAddSource(streamInfo, options, ++retryTime);
    }
    getCodec() {
        return new Promise((resolve, reject) => {
            if (this.codec)
                resolve(this.codec);
            let timeout = { time: 0 };
            const inetervalHandler = setInterval(() => {
                timeout.time++;
                console.log("getCodec times:", timeout.time);
                if (timeout.time > 5) {
                    clearInterval(inetervalHandler);
                    this.codec = VideoCodec.H264;
                    console.log("use default codec:H264");
                    resolve(this.codec);
                }
                //@ts-ignore
                if (window.WebRTCVideoCodec) {
                    //@ts-ignore
                    console.log("getCodec success", window.WebRTCVideoCodec);
                    //@ts-ignore
                    this.codec = window.WebRTCVideoCodec;
                    clearInterval(inetervalHandler);
                    resolve(this.codec);
                }
            }, 1000);
        });
    }
    initController(nvrId, deviceIdV2, orgId) {
        if (!this.connectingPromise) {
            //save promise singleton for further use
            let attemptRef = { attempt: 0 };
            const creationSingleton = () => {
                return new Promise(async (resolve, reject) => {
                    await this.getCodec();
                    if (!this.turn || !this.rtcToken) {
                        try {
                            let result = await Signal.getTurns(ContextArgs.token, orgId, this.nvrId, ContextArgs.sessionId, deviceIdV2);
                            if (result.err) {
                                throw new Error(result.err.message);
                            }
                            let data = result.ret;
                            this.turn = data.turn;
                            this.rtcToken = data.token;
                        }
                        catch (error) {
                            console.error("error to fetch credentials", error, "nvrId", nvrId, 'deviceIdV2', deviceIdV2);
                            reject(new Error("Invalid response"));
                            return;
                        }
                    }
                    // this function is called when closed and error occured
                    const onErrorRetry = (err) => {
                        console.log("onClose", err, "nvrId", nvrId);
                        if (err === "normal") {
                            return void 0;
                        }
                        if (attemptRef.attempt > ContextArgs.retryLimit) {
                            // broadcast error state to all streams
                            this.sourceIdCbMap.forEach((value, key) => {
                                value.state = STREAM_STATE.ERROR;
                                const error = new AppError({ code: AppError.CODE_WEBRTC_DISCONNECTED, name: AppError.NAME_WEBRTC_DISCONNECTED, message: 'webRTC retry limit reached' });
                                value.options.onStateChange?.(STREAM_STATE.ERROR, value, error);
                            });
                            console.log("webRTC retry limit reached,nvrId", nvrId);
                            // this._controller = null
                            this.connectingPromise = null;
                            // reset init state
                            this.inited = false;
                            reject(err);
                            return void 0;
                        }
                        else {
                            // broadcast retry state to all streams
                            this.sourceIdCbMap.forEach((value, key) => {
                                value.state = STREAM_STATE.WEBRTC_RETRYING;
                                value.options.onStateChange?.(STREAM_STATE.WEBRTC_RETRYING, value);
                            });
                            console.log("webRTC retry attempt: ", attemptRef.attempt, "nvrId", nvrId);
                            attemptRef.attempt++;
                            // important to make promise synchronously, otherwise the promise might be overwritten
                            const _promise = new Promise((_resolve, _reject) => {
                                // make a interval for each retry
                                setTimeout(() => {
                                    //1. one way to make connectingPromise is below here in retry
                                    //2. another is trigger initController
                                    const innerPromise = creationSingleton();
                                    innerPromise.then((controller) => {
                                        // this resolve make iterator to continue, resolve connectingPromise at situation 2 mentioned above
                                        resolve(controller);
                                        // this resolve connectingPromise to be resolved at situation 1 mentioned above
                                        _resolve(controller);
                                    })
                                        .catch((e) => {
                                        reject(e);
                                    });
                                }, ContextArgs.retryInterval || 1000);
                            });
                            if (!this.connectingPromise) {
                                this.connectingPromise = _promise;
                            }
                        }
                    };
                    const onControllerConnected = (controller) => {
                        attemptRef.attempt = 0;
                        console.log("controller full ready,nvrId", nvrId);
                        this.connectingPromise = null;
                        this._controller = controller;
                        this.bindEventsProxy(controller);
                        const sources = [];
                        //construct new canvas and replace
                        this.sourceIdCbMap.forEach((value, key) => {
                            value.streamInfo && sources.push(value.streamInfo);
                            const originalCanvas = value.streamInfo.canvas;
                            if (originalCanvas) {
                                const newCanvas = value.streamInfo.canvas.cloneNode(true);
                                value.streamInfo.canvas = newCanvas;
                            }
                        });
                        if (sources.length) {
                            console.log("start addSources", sources);
                            controller.addSources(sources, (infos) => {
                                if (infos?.streamInfo?.sourceId) {
                                    // find the target stream
                                    const id = infos?.streamInfo?.sourceId;
                                    const sourceIdInner = secondLastIndexOf(id, '-');
                                    const target = this.sourceIdCbMap.get(sourceIdInner);
                                    // the stream is deleted
                                    if (!target)
                                        return;
                                    this.handleControllerRes(infos, target);
                                }
                            });
                        }
                        resolve(controller);
                    };
                    const controller = new GeneralController({
                        orgId: orgId,
                        nvrId: +nvrId,
                        signal: ContextArgs.signal,
                        turn: this.turn,
                        rtcToken: this.rtcToken,
                        debugging: Boolean(ContextArgs.ENV && ContextArgs.ENV === "development"),
                        //@ts-ignore
                        workerDebug: window.webRTCDebug,
                        videoCodec: this.codec,
                    });
                    let eventReady = false;
                    let msgOpen = false;
                    let extraChannelOpen = false;
                    controller.addEventListener(GeneralController.EVENT_READY, async () => {
                        eventReady = true;
                        if (msgOpen && extraChannelOpen) {
                            onControllerConnected(controller);
                        }
                    });
                    controller.webRTC.addEventListener(WebRTC.EVENT_MESSAGE_OPEN, async () => {
                        msgOpen = true;
                        if (eventReady && extraChannelOpen) {
                            onControllerConnected(controller);
                        }
                    });
                    controller.webRTC.addEventListener(DataWebRTC.EXTRA_CHANNEL_CONNECTED, async () => {
                        extraChannelOpen = true;
                        if (eventReady && msgOpen) {
                            onControllerConnected(controller);
                        }
                    });
                    controller.webRTC.addEventListener(WebRTCBase.ACTION_FIRST_FRAME, (data) => {
                        const id = secondLastIndexOf(data.data.id, "-");
                        const target = this.sourceIdCbMap.get(id);
                        target?.options?.onStateChange?.(STREAM_STATE.PLAYING, target);
                    });
                    controller.addEventListener(StreamController.EVENT_TIME_PROGRESS, (currentOffset, progress, sourceId) => {
                        const id = secondLastIndexOf(sourceId, "-");
                        const target = this.sourceIdCbMap.get(id);
                        target?.options?.onProgress?.(currentOffset);
                    });
                    controller.webRTC.addEventListener(AudioWebRTC.EVENT_SPEAKABLE, ({ speakMuted, kind, error, sourceId, }) => {
                        const id = secondLastIndexOf(sourceId, "-");
                        const target = this.sourceIdCbMap.get(id);
                        target?.options?.onStateChange?.(STREAM_STATE.AUDIO_SPEAKABLE, { speakMuted, kind, error });
                    });
                    controller.addEventListener(GeneralController.EVENT_CLOSE, onErrorRetry);
                    controller.startV2([]);
                });
            };
            try {
                const promise = creationSingleton();
                this.connectingPromise = promise;
                return promise;
            }
            catch (e) {
                console.error(e);
                this.connectingPromise = null;
                throw e;
            }
        }
        return this.connectingPromise;
    }
}
